import { cloneElement, isValidElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Popup } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER, RADIUS } from './internal/presets'
import { useTrigger } from './internal/useTrigger'
import Button from './Button'

const DropdownMenu = ({ items = [], onClick, onClose }) => (
  <ul role="menu" className="m-0 list-none p-1">
    {items.filter(Boolean).map((item) => {
      if (item.type === 'divider') {
        return <li key={item.key} role="separator" className={cn('my-1 border-t', BORDER)} />
      }
      return (
        <li key={item.key} role="none">
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return
              item.onClick?.({ key: item.key })
              onClick?.({ key: item.key, item })
              onClose()
            }}
            className={cn(
              'ui-dropdown-menu-item flex min-h-[var(--control-height-sm)] w-full items-center gap-2 rounded-[var(--radius-xs)] px-3 text-left text-sm',
              item.danger ? 'text-[var(--error-color)]' : 'text-[var(--text-body)]',
              item.disabled
                ? 'cursor-not-allowed opacity-50'
                : 'hover:bg-[var(--color-primary-50)] hover:text-[var(--primary-color)]'
            )}
          >
            {item.icon}
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
          </button>
        </li>
      )
    })}
  </ul>
)

/**
 * antd Dropdown. Accepts `menu={{ items, onClick }}` (v5), the legacy `overlay`
 * node, and `Dropdown.Button`.
 */
const Dropdown = ({
  menu,
  overlay,
  dropdownRender,
  placement = 'bottomLeft',
  trigger = 'hover',
  open,
  onOpenChange,
  disabled = false,
  arrow,
  overlayClassName,
  overlayStyle,
  zIndex,
  children,
  ...rest
}) => {
  const { isOpen, close, anchorRef, popupRef, anchorProps, popupHoverProps } = useTrigger({
    trigger,
    open,
    onOpenChange,
    disabled,
  })

  const child = isValidElement(children) ? (
    cloneElement(children, {
      ref: anchorRef,
      'aria-haspopup': 'menu',
      'aria-expanded': isOpen,
      ...anchorProps,
    })
  ) : (
    <span ref={anchorRef} {...anchorProps}>
      {children}
    </span>
  )

  const content = overlay
    ? typeof overlay === 'function'
      ? overlay()
      : overlay
    : <DropdownMenu items={menu?.items} onClick={menu?.onClick} onClose={close} />

  return (
    <>
      {child}
      <Popup
        anchorRef={anchorRef}
        popupRef={popupRef}
        open={isOpen}
        placement={placement}
        zIndex={zIndex}
        className={cn(
          'ui-dropdown min-w-[160px] border bg-[var(--surface-card)] shadow-[var(--shadow-lg)]',
          BORDER,
          RADIUS,
          overlayClassName
        )}
        style={overlayStyle}
        {...popupHoverProps}
        {...rest}
      >
        {dropdownRender ? dropdownRender(content) : content}
      </Popup>
    </>
  )
}

const DropdownButton = ({ menu, icon, onClick, children, buttonsRender, ...rest }) => {
  const { t } = useTranslation()
  return (
  <span className="ui-dropdown-button inline-flex items-stretch">
    <Button onClick={onClick} className="rounded-r-none">
      {children}
    </Button>
    <Dropdown menu={menu} trigger="click" {...rest}>
      <Button className="-ml-px rounded-l-none px-2" aria-label={t('common.moreActions')}>
        {icon ?? <span aria-hidden="true">...</span>}
      </Button>
    </Dropdown>
  </span>
  )
}

Dropdown.Button = DropdownButton

export default Dropdown
